import { Box, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

const isEmbedded = (variant) => variant === 'embedded';

export const RegulatorySection = styled(Box)(() => ({ width: '100%' }));

export const RegulatoryDetails = styled('details', {
  shouldForwardProp: (prop) => !['displayVariant', 'isLast'].includes(prop),
})(({ theme, displayVariant, isLast }) => {
  const embedded = isEmbedded(displayVariant);
  const lineColor = embedded
    ? alpha(theme.palette.common.white, 0.16)
    : alpha(theme.palette.text.primary, 0.12);

  return displayVariant === 'card'
    ? {
        border: `1px solid ${lineColor}`,
        borderRadius: theme.shape.borderRadius * 2.5,
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        '& + &': { marginTop: theme.spacing(1) },
        '&[open]': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: alpha(theme.palette.primary.main, 0.025),
        },
        '&[open] .accordionIcon': { transform: 'rotate(180deg)' },
      }
    : {
        borderBottom: isLast ? 'none' : `1px solid ${lineColor}`,
        '&[open] .accordionIcon': { transform: 'rotate(45deg)' },
      };
});

export const RegulatorySummary = styled('summary', {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ theme, displayVariant }) => {
  const embedded = isEmbedded(displayVariant);
  const card = displayVariant === 'card';

  return {
    listStyle: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minHeight: card ? 50 : 44,
    padding: theme.spacing(1, card ? 1.75 : 0.75),
    margin: card ? 0 : theme.spacing(0, -0.75),
    borderRadius: card ? 0 : theme.shape.borderRadius,
    color: embedded ? theme.palette.common.white : theme.palette.text.primary,
    transition: 'background-color 0.2s ease',
    '&::-webkit-details-marker': { display: 'none' },
    '&:hover, &:focus-visible': {
      backgroundColor: embedded
        ? alpha(theme.palette.common.white, 0.12)
        : alpha(theme.palette.primary.main, 0.08),
      outline: 'none',
    },
  };
});

export const RegulatoryTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ displayVariant }) => ({
  fontSize: displayVariant === 'embedded' ? 'clamp(14px, calc(0.278vw + 10.67px), 16px)' : 14,
  fontWeight: 700,
  lineHeight: 1.35,
}));

export const AccordionIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ theme, displayVariant }) => ({
  color: isEmbedded(displayVariant)
    ? alpha(theme.palette.common.white, 0.56)
    : theme.palette.text.secondary,
  fontSize: displayVariant === 'card' ? 21 : 18,
  lineHeight: 1,
  flex: '0 0 auto',
  display: 'flex',
  transition: 'transform 0.2s ease',
}));

export const RegulatoryContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ theme, displayVariant }) => {
  const embedded = isEmbedded(displayVariant);
  const card = displayVariant === 'card';
  const linkColor = embedded ? theme.palette.common.white : theme.palette.primary.main;

  return {
    color: embedded ? alpha(theme.palette.common.white, 0.8) : theme.palette.text.secondary,
    fontSize: embedded ? 'clamp(13px, calc(0.278vw + 9.67px), 15px)' : 13,
    lineHeight: 1.6,
    paddingLeft: theme.spacing(card ? 1.75 : 0.75),
    paddingRight: theme.spacing(card ? 1.75 : 0.75),
    paddingTop: card ? theme.spacing(0.25) : 0,
    paddingBottom: theme.spacing(card ? 1.75 : 1.5),
    '& a': {
      color: linkColor,
      textDecorationColor: alpha(linkColor, 0.72),
      textUnderlineOffset: '3px',
    },
  };
});

export const DetailRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  '& + &': { marginTop: 8 },
}));

export const DetailIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ theme, displayVariant }) => ({
  display: 'flex',
  flex: '0 0 auto',
  marginTop: 2,
  color: isEmbedded(displayVariant)
    ? alpha(theme.palette.common.white, 0.55)
    : theme.palette.text.secondary,
  '& svg': {
    fontSize: displayVariant === 'embedded' ? 'clamp(15px, calc(0.278vw + 11.67px), 17px)' : 15,
  },
}));

export const ManufacturerName = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'displayVariant',
})(({ theme, displayVariant }) => ({
  color: isEmbedded(displayVariant) ? theme.palette.common.white : theme.palette.text.primary,
  fontWeight: 700,
  marginBottom: theme.spacing(1),
}));

export const IdentifierGrid = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'max-content max-content max-content',
  columnGap: theme.spacing(1.25),
  rowGap: theme.spacing(0.25),
  color: alpha(theme.palette.common.white, 0.6),
  fontSize: 11,
  lineHeight: 1.7,
  [theme.breakpoints.up('lg')]: {
    marginTop: theme.spacing(5),
    fontSize: 'clamp(11px, calc(0.417vw + 6px), 14px)',
  },
}));

export const IdentifierValue = styled('span')(() => ({
  fontVariantNumeric: 'tabular-nums',
}));
